import React, { useEffect, useRef, useState } from "react";
import { ListItem } from "@src/types/ota-update";
import { useSortableOtaHistoryList } from "./use-sortable-ota-history-list";
import { useI18N } from "@src/language/language-context";
interface ResizingState {
  handleKey: string;
  leftCol: keyof ListItem;
  rightCol: keyof ListItem;
  startX: number;
  startLeftWidth: number;
  startRightWidth: number;
}

interface UseOtaHistoryListProps {
  list: ListItem[] | [];
  setSelectedRow: React.Dispatch<React.SetStateAction<ListItem | null>>;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useOtaHistoryList = ({
  list,
  setSelectedRow,
  setIsModalOpen,
}: UseOtaHistoryListProps) => {
  const i18n = useI18N();
  const [columnWidths, setColumnWidths] = useState({
    id: 0,
    filename: 20,
    version: 13,
    digest: 17,
    size_bytes: 10,
    uploader: 23,
    uploaded_at: 17,
  });

  const [resizing, setResizing] = useState<ResizingState | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const { sortedRows, sortConfig, handleSort } =
    useSortableOtaHistoryList<ListItem>(list);

  const texts = {
    filename: i18n("modules.otaHistoryList.firmwareName"),
    version: i18n("modules.otaHistoryList.version"),
    digest: i18n("modules.otaHistoryList.digest"),
    size_bytes: i18n("modules.otaHistoryList.size"),
    uploader: i18n("modules.otaHistoryList.uploader"),
    upload_time: i18n("modules.otaHistoryList.uploadTime"),
  };

  const columns: { key: keyof ListItem; label: string }[] = [
    { key: "filename", label: texts.filename },
    { key: "version", label: texts.version },
    { key: "digest", label: texts.digest },
    { key: "size_bytes", label: texts.size_bytes },
    { key: "uploader", label: texts.uploader },
    { key: "uploaded_at", label: texts.upload_time },
  ];

  const handleMouseDown = (
    e: React.MouseEvent,
    leftColKey: keyof ListItem,
    rightColKey: keyof ListItem,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setResizing({
      handleKey: rightColKey,
      leftCol: leftColKey,
      rightCol: rightColKey,
      startX: e.clientX,
      startLeftWidth: columnWidths[leftColKey],
      startRightWidth: columnWidths[rightColKey],
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing || !tableRef.current) return;
      const deltaX = e.clientX - resizing.startX;
      const tableWidth = tableRef.current.offsetWidth;
      const deltaPercent = (deltaX / tableWidth) * 100;
      const newLeftWidth = resizing.startLeftWidth + deltaPercent;
      const newRightWidth = resizing.startRightWidth - deltaPercent;

      if (newLeftWidth >= 5 && newRightWidth >= 5) {
        setColumnWidths((prev) => ({
          ...prev,
          [resizing.leftCol]: newLeftWidth,
          [resizing.rightCol]: newRightWidth,
        }));
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const handleRowClick = (row: ListItem) => {
    setSelectedRow(row);
    setIsModalOpen?.(true);
  };

  return {
    tableRef,
    columnWidths,
    resizing,
    sortedRows,
    sortConfig,
    handleSort,
    handleMouseDown,
    handleRowClick,
    columns,
  };
};
